import { getParsedSvg } from "lib/svg/svg";

export const HEAVY_SVG_NODE_THRESHOLD = 2000;

interface OutlineLike {
  width?: number | string;
  fade?: number | string;
}

interface HeavyVectorElementData {
  src?: string;
  outline?: OutlineLike;
}

const isTruthyOutlineValue = (value: number | string | undefined): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === "number") return value !== 0;
  return value !== "" && value !== "0";
};

// Descendant count per cached parsed root. The parsed root is immutable after
// setParsedSvg, so the count is too — cache it so the O(n) tree walk runs
// once per src instead of on every render/drag frame that asks "is heavy?".
const descendantCountCache = new WeakMap<Element, number>();

// Sticky per-src "is heavy" decision. Once an src has been seen with a parsed
// root, the outcome is memoised here so subsequent calls remain correct even
// if the parsed DOM cache is later cleared (reset in tests, GC of unreferenced
// roots) or the caller hits the pre-parse window on a slow re-mount.
const heavinessBySrc = new Map<string, boolean>();

const getDescendantCount = (root: Element): number => {
  const cached = descendantCountCache.get(root);
  if (cached !== undefined) return cached;
  const count = root.getElementsByTagName("*").length;
  descendantCountCache.set(root, count);
  return count;
};

export const __resetHeavyVectorDecisionCache = (): void => {
  heavinessBySrc.clear();
};

// Pure descendant-count check, ignoring the outline carve-out. Use this when
// the calling path renders a static thumbnail (no outline drawn either way),
// so an outlined heavy vector still benefits from skipping live <Vector>.
export const hasHeavySvgTree = (
  elementData: HeavyVectorElementData
): boolean => {
  if (!elementData || !elementData.src) return false;

  const root = getParsedSvg(elementData.src);
  if (!root) {
    // Pre-parse window (initial fetch in flight, cache reset, etc). Fall back
    // to the sticky decision so a previously-known heavy src is still treated
    // as heavy while the Vector's parse pipeline catches up.
    return heavinessBySrc.get(elementData.src) ?? false;
  }

  const isHeavy = getDescendantCount(root) > HEAVY_SVG_NODE_THRESHOLD;
  heavinessBySrc.set(elementData.src, isHeavy);
  return isHeavy;
};

export const isHeavyVector = (elementData: HeavyVectorElementData): boolean => {
  if (!elementData) return false;

  const outline = elementData.outline;
  if (
    outline &&
    (isTruthyOutlineValue(outline.width) || isTruthyOutlineValue(outline.fade))
  ) {
    // The heavy drag/resize codepath can't preserve outline rendering, so an
    // outlined heavy vector must stay on the live-render path there.
    return false;
  }

  return hasHeavySvgTree(elementData);
};
